import React, { useEffect, useState } from 'react';
import styles from 'styles/components/WorkspaceSection.module.sass';
import PrimaryButton from 'components/common/PrimaryButton';
import SecondaryButton from 'components/common/SecondaryButton';
import platformImage from 'assets/images/png/analytiqs-platform.png';
import PilotEndedDialog from 'components/cards/PilotEndedDialog';
import { useAuth } from 'hooks/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'provider/ToastProvider';
import endpoints from 'constants/api/Endpoints';
import { logError } from 'services/logService/errorLogger';
import { trackSessionEnd } from '../../tracking';

type WorkspaceState =
    | 'checking'              // Auto-checking subscription on mount
    | 'workspace_not_open'    // Feature flag disabled — workspace not yet public
    | 'unauthenticated'       // Not logged in
    | 'subscription_required' // Logged in but no active paid subscription
    | 'org_inactive'          // Invited member — org owner's subscription has lapsed
    | 'email_not_verified'    // Account exists but email hasn't been verified yet
    | 'pilot_expired'         // Former Pilot participant — 4-week access window has ended
    | 'account_suspended'     // Account is suspended, revoked or deleted
    | 'redirecting'           // Token obtained — about to open workspace
    | 'error'                 // Unexpected failure

const FEATURES = [
    'AI-Powered Analysis',
    'Interactive Dashboards',
    'Written Reports',
    'ZeroneBot Assistant',
    'Data Visualization',
    'Presentation Builder',
];

const WorkspaceSection: React.FC = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { addToast } = useToast();

    const [state, setState] = useState<WorkspaceState>('checking');
    const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
    const [planName, setPlanName] = useState<string | undefined>();
    const [pilotDialogOpen, setPilotDialogOpen] = useState(true);

    useEffect(() => {
        if (authLoading) return;

        const requestWorkspaceAccess = async () => {
            setState('checking');
            try {
                const flag = await endpoints.fetchWorkspaceFlag({ tag: 'WorkspaceSection.flag' });
                if (!flag.enabled) {
                    setState('workspace_not_open');
                    return;
                }

                if (!user) {
                    setState('unauthenticated');
                    return;
                }

                const result = await endpoints.fetchWorkspaceToken({ tag: 'WorkspaceSection' });

                if (result.success && result.data) {
                    setState('redirecting');
                    const { workspaceUrl, token, displayName, avatarUrl } = result.data;
                    // Hash fragment — never sent to the server, never written to
                    // access logs. The platform strips it client-side on arrival.
                    const params = new URLSearchParams({ token });
                    if (displayName) params.set('displayName', displayName);
                    if (avatarUrl)   params.set('avatarUrl', avatarUrl);
                    // Close the corporate tracking session as a hand-off (a success, not an exit).
                    // Bounded wait: the redirect is never held up for more than half a second.
                    await trackSessionEnd('handoff', 500);
                    window.location.href = `${workspaceUrl}#${params.toString()}`;
                    return;
                }

                if (result.error === 'email_not_verified')    { setState('email_not_verified'); return; }
                if (result.error === 'must_change_password')  { navigate('/change-password-required', { state: { redirect: '/workspace' } }); return; }
                if (result.error === 'org_inactive')          { setState('org_inactive'); return; }
                if (result.error === 'pilot_expired')         { setState('pilot_expired'); return; }
                if (result.error === 'account_suspended')     { setState('account_suspended'); return; }
                if (result.error === 'subscription_required') {
                    setState('subscription_required');
                    setPaymentStatus(result.paymentStatus);
                    setPlanName(result.planName);
                    return;
                }
                if (result.error === 'unauthorized') { setState('unauthenticated'); return; }

                setState('error');
            } catch (err) {
                logError(
                    'WorkspaceSection: unexpected error during workspace token request.',
                    { error: err instanceof Error ? err.message : 'Unknown error' },
                    'WorkspaceSection'
                );
                setState('error');
            }
        };

        requestWorkspaceAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading]);

    const handleLogIn       = () => navigate('/login', { state: { redirect: '/workspace' } });
    const handleSignUp      = () => navigate('/sign-up');
    const handleUpgrade     = () => navigate('/pricing');
    const handleMakePayment = () => navigate('/subscription/payment');
    const handleVerifyEmail = () => navigate('/verify-email');
    const handleContactSupport = () => navigate('/contact-us');

    const handleRetry = async () => {
        setState('checking');
        try {
            const flag = await endpoints.fetchWorkspaceFlag({ tag: 'WorkspaceSection.retry.flag' });
            if (!flag.enabled) {
                setState('workspace_not_open');
                return;
            }

            const result = await endpoints.fetchWorkspaceToken({ tag: 'WorkspaceSection.retry' });
            if (result.success && result.data) {
                setState('redirecting');
                const { workspaceUrl, token, displayName, avatarUrl } = result.data;
                // Hash fragment — never sent to the server, never written to
                // access logs. The platform strips it client-side on arrival.
                const params = new URLSearchParams({ token });
                if (displayName) params.set('displayName', displayName);
                if (avatarUrl)   params.set('avatarUrl', avatarUrl);
                await trackSessionEnd('handoff', 500);
                window.location.href = `${workspaceUrl}#${params.toString()}`;
            } else if (result.error === 'email_not_verified') {
                setState('email_not_verified');
            } else if (result.error === 'must_change_password') {
                navigate('/change-password-required', { state: { redirect: '/workspace' } });
            } else if (result.error === 'org_inactive') {
                setState('org_inactive');
            } else if (result.error === 'pilot_expired') {
                setState('pilot_expired');
            } else if (result.error === 'account_suspended') {
                setState('account_suspended');
            } else if (result.error === 'subscription_required') {
                setState('subscription_required');
                setPaymentStatus(result.paymentStatus);
            } else {
                setState('error');
                addToast({ type: 'error', message: 'Unable to access workspace. Please try again later.' });
            }
        } catch {
            setState('error');
            addToast({ type: 'error', message: 'An error occurred. Please try again.' });
        }
    };

    // ── Shared hero image ─────────────────────────────────────────────────────

    const HeroImage = () => (
        <div className={styles.imageContainer}>
            <img src={platformImage} alt="ZeroneAnalytiQs Platform" className={styles.heroImage} />
        </div>
    );

    // ── Loading / redirecting ─────────────────────────────────────────────────

    if (state === 'checking' || state === 'redirecting') {
        return (
            <section className={styles.workspaceSection}>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles['status-badge']}>
                            <span className={styles['status-badge-dot']} />
                            Platform Live
                        </div>
                        <div className={styles.headline}>
                            <span className={styles['headline-main']}>Your AI Analytics</span>
                            <span className={styles['headline-accent']}>Workspace</span>
                        </div>
                        <div className={styles['loader-container']}>
                            <span className={styles['spinner']} />
                            <span className={styles['loader-text']}>
                                {state === 'redirecting' ? 'Opening your workspace…' : 'Verifying your access…'}
                            </span>
                        </div>
                    </div>
                    <HeroImage />
                </div>
            </section>
        );
    }

    // ── Workspace not open ────────────────────────────────────────────────────

    if (state === 'workspace_not_open') {
        return (
            <section className={styles.workspaceSection}>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles['status-badge']}>
                            <span className={styles['status-badge-dot']} />
                            Coming Soon
                        </div>
                        <div className={styles.headline}>
                            <span className={styles['headline-main']}>Your AI Analytics</span>
                            <span className={styles['headline-accent']}>Workspace</span>
                        </div>
                        <p className={styles.description}>
                            We&apos;re putting the finishing touches on your workspace. It will be available
                            very soon - check back shortly for full access to AI-powered African market analytics.
                        </p>
                        <div className={styles['features-list']}>
                            {FEATURES.map(f => (
                                <span key={f} className={styles['feature-pill']}>
                                    <span className={styles['feature-pill-dot']} />
                                    {f}
                                </span>
                            ))}
                        </div>
                    </div>
                    <HeroImage />
                </div>
            </section>
        );
    }

    // ── Unauthenticated ───────────────────────────────────────────────────────

    if (state === 'unauthenticated') {
        return (
            <section className={styles.workspaceSection}>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles['status-badge']}>
                            <span className={styles['status-badge-dot']} />
                            Platform Live
                        </div>
                        <div className={styles.headline}>
                            <span className={styles['headline-main']}>Your AI Analytics</span>
                            <span className={styles['headline-accent']}>Workspace</span>
                        </div>
                        <p className={styles.description}>
                            Sign in to access your private analytics canvas. Analyse African market data,
                            build AI-powered reports, and explore insights — all in one place.
                        </p>
                        <div className={styles['features-list']}>
                            {FEATURES.map(f => (
                                <span key={f} className={styles['feature-pill']}>
                                    <span className={styles['feature-pill-dot']} />
                                    {f}
                                </span>
                            ))}
                        </div>
                        <div className={styles.form}>
                            <PrimaryButton
                                text="Log In to Access Workspace"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={handleLogIn}
                            />
                            <SecondaryButton
                                text="Create an Account"
                                primaryColor="transparent"
                                textColor="#FFFFFF"
                                hoverColor="#FF5522"
                                hoverTextColor="#FFFFFF"
                                doSomething={handleSignUp}
                            />
                        </div>
                    </div>
                    <HeroImage />
                </div>
            </section>
        );
    }

    // ── Pilot expired ─────────────────────────────────────────────────────────

    if (state === 'pilot_expired') {
        return (
            <section className={styles.workspaceSection}>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles.headline}>
                            <span className={styles['headline-main']}>Unlock Your</span>
                            <span className={styles['headline-accent']}>Analytics Workspace</span>
                        </div>
                        <p className={styles.description}>
                            Your Pilot access has ended. Subscribe to a plan to continue using the
                            ZeroneAnalytiQs workspace.
                        </p>
                        <div className={styles['features-list']}>
                            {FEATURES.map(f => (
                                <span key={f} className={styles['feature-pill']}>
                                    <span className={styles['feature-pill-dot']} />
                                    {f}
                                </span>
                            ))}
                        </div>
                        <div className={styles.form}>
                            <PrimaryButton
                                text="View Subscription Plans"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={handleUpgrade}
                            />
                        </div>
                    </div>
                    <HeroImage />
                </div>
                <PilotEndedDialog
                    isOpen={pilotDialogOpen}
                    onClose={() => setPilotDialogOpen(false)}
                />
            </section>
        );
    }

    // ── Account suspended ─────────────────────────────────────────────────────

    if (state === 'account_suspended') {
        return (
            <section className={styles.workspaceSection}>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles.headline}>
                            <span className={styles['headline-main']}>Account</span>
                            <span className={styles['headline-accent']}>Suspended</span>
                        </div>
                        <p className={styles.description}>
                            Your account has been suspended. Please contact our support team
                            for more information.
                        </p>
                        <div className={styles.form}>
                            <PrimaryButton
                                text="Contact Support"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={handleContactSupport}
                            />
                        </div>
                    </div>
                    <HeroImage />
                </div>
            </section>
        );
    }

    // ── Org inactive ──────────────────────────────────────────────────────────

    if (state === 'org_inactive') {
        return (
            <section className={styles.workspaceSection}>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles.headline}>
                            <span className={styles['headline-main']}>Workspace</span>
                            <span className={styles['headline-accent']}>Access Paused</span>
                        </div>
                        <p className={styles.description}>
                            Your organisation&apos;s subscription is no longer active.
                            Please contact your account owner to restore access to the workspace.
                        </p>
                    </div>
                    <HeroImage />
                </div>
            </section>
        );
    }

    // ── Email not verified ────────────────────────────────────────────────────

    if (state === 'email_not_verified') {
        return (
            <section className={styles.workspaceSection}>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles.headline}>
                            <span className={styles['headline-main']}>Verify Your</span>
                            <span className={styles['headline-accent']}>Email Address</span>
                        </div>
                        <p className={styles.description}>
                            You need to verify your email address to access the workspace.
                            Check your inbox for a verification link, then come back to continue.
                        </p>
                        <div className={styles.form}>
                            <PrimaryButton
                                text="Verify Email"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={handleVerifyEmail}
                            />
                        </div>
                    </div>
                    <HeroImage />
                </div>
            </section>
        );
    }

    // ── Subscription required ─────────────────────────────────────────────────

    if (state === 'subscription_required') {
        const isPendingPayment =
            paymentStatus === 'pending' ||
            paymentStatus === 'overdue'  ||
            paymentStatus === 'failed';

        return (
            <section className={styles.workspaceSection}>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles.headline}>
                            <span className={styles['headline-main']}>Unlock Your</span>
                            <span className={styles['headline-accent']}>Analytics Workspace</span>
                        </div>
                        <p className={styles.description}>
                            {isPendingPayment
                                ? `Your ${planName ? `${planName} ` : ''}subscription payment is pending. Complete your payment to unlock full workspace access.`
                                : 'An active subscription is required to access the ZeroneAnalytiQs workspace. Choose a plan to get started with AI-powered African market analytics.'}
                        </p>
                        <div className={styles['features-list']}>
                            {FEATURES.map(f => (
                                <span key={f} className={styles['feature-pill']}>
                                    <span className={styles['feature-pill-dot']} />
                                    {f}
                                </span>
                            ))}
                        </div>
                        <div className={styles.form}>
                            {isPendingPayment ? (
                                <PrimaryButton
                                    text="Complete Payment"
                                    primaryColor="#FF5522"
                                    textColor="#FFFFFF"
                                    hoverColor="#FFFFFF"
                                    hoverTextColor="#FF5522"
                                    doSomething={handleMakePayment}
                                />
                            ) : (
                                <PrimaryButton
                                    text="View Subscription Plans"
                                    primaryColor="#FF5522"
                                    textColor="#FFFFFF"
                                    hoverColor="#FFFFFF"
                                    hoverTextColor="#FF5522"
                                    doSomething={handleUpgrade}
                                />
                            )}
                        </div>
                    </div>
                    <HeroImage />
                </div>
            </section>
        );
    }

    // ── Error ─────────────────────────────────────────────────────────────────

    return (
        <section className={styles.workspaceSection}>
            <div className={styles.container}>
                <div className={styles.content}>
                    <div className={styles.headline}>
                        <span className={styles['headline-main']}>Something Went</span>
                        <span className={styles['headline-accent']}>Wrong</span>
                    </div>
                    <p className={styles.description}>
                        We couldn&apos;t connect to your workspace. Please try again — if the issue
                        persists, contact our support team.
                    </p>
                    <div className={styles.form}>
                        <PrimaryButton
                            text="Try Again"
                            primaryColor="#FF5522"
                            textColor="#FFFFFF"
                            hoverColor="#FFFFFF"
                            hoverTextColor="#FF5522"
                            doSomething={() => { void handleRetry(); }}
                        />
                    </div>
                </div>
                <HeroImage />
            </div>
        </section>
    );
};

export default WorkspaceSection;
